import { getCartCookie } from "@/utils/cart/getCartCookie";
import { useMarket } from "@app/_context";
import type { Cart } from "@server/gql/graphql";
import { clearCart as _clearCart } from "@server/requests/checkout/clearCart";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

type ClearCart = <T extends boolean = false>(
  async?: T,
) => T extends true ? Promise<Cart | undefined> : void;

export default function useClearCart() {
  const { serviceLocale: locale } = useMarket().state.market;
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isError, isPending, isSuccess } = useMutation({
    mutationFn: async () => {
      const cartId = getCartCookie()?.value;
      if (!cartId) return Promise.resolve(undefined);
      return _clearCart({
        locale,
        cartId,
      });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["cart"] });
      await queryClient.cancelQueries({ queryKey: ["checkout"] });
      await queryClient.cancelQueries({ queryKey: ["checkoutDelivery"] });
      await queryClient.cancelQueries({ queryKey: ["payment"] });

      const previousCart = queryClient.getQueryData<Cart>(["cart"]);

      queryClient.setQueryData<Cart>(["cart"], (currentCart) => {
        if (!currentCart) {
          return;
        }
        return {
          ...currentCart,
          items: [],
          totalGrossAmount: 0,
        };
      });

      return { previousCart };
    },
    onError: (
      error,
      input,
      context: { previousCart: Cart | undefined } | undefined,
    ) => {
      if (context?.previousCart) {
        queryClient.setQueryData<Cart>(["cart"], context.previousCart);
      }
    },
    onSettled: async () => {
      void queryClient.invalidateQueries({ queryKey: ["cart"] });
      void queryClient.invalidateQueries({ queryKey: ["checkout"] });
      void queryClient.invalidateQueries({ queryKey: ["checkoutDelivery"] });
      await queryClient.invalidateQueries({ queryKey: ["payment"] });
    },
  });

  const clearCart = useCallback(
    (async = false) => {
      if (async) return mutateAsync();
      mutate();
    },
    [mutate, mutateAsync],
  );

  return {
    clearCart: clearCart as ClearCart,
    isErrorClearCart: isError,
    isPendingClearCart: isPending,
    isSuccessClearCart: isSuccess,
  };
}
